import { FC, useCallback, useEffect, useState } from 'react';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import Loader from '../../../../components/loader/index';
import { useCanDownloadSpace } from '../../hooks/use-can-download-space.hook';
import { useDownloadSpaceFile } from '../../hooks/use-download-space-file.hook';
import { FilePreviewState, useSpaceFilePreview } from '../../hooks/use-space-file-preview.hook';
import { FilePreviewStore } from '../../store/file-preview.store';
import { FilePreviewDocument, PAGE_WIDTH_PX } from './file-preview-document';
import { FilePreviewDocx } from './file-preview-docx';
import { FilePreviewHeader } from './file-preview-header';
import { FilePreviewSheet } from './file-preview-sheet';
import { PreviewKind, previewKindOf } from './preview-kind.const';
import { DEFAULT_ZOOM, zoomIn, zoomOut } from './zoom.const';

/**
 * The file viewer — the design's `Preview File`, over everything.
 *
 * Full screen rather than a dialog in the middle: a document is read, and a box in the centre of
 * a page the reader cannot use anyway wastes the room the reading needs. The page behind stays
 * where it was, blurred under a dark scrim, so closing puts the reader back in their folder.
 *
 * One shell, two renderers. A PDF is drawn page by page and says how many it has; an image is one
 * page and knows it, which is why the counter is hidden for an image rather than reading "1 / 1".
 * Both answer the same zoom.
 */
export const FilePreviewOverlay: FC = () => {
  const file = FilePreviewStore(({ file }) => file);
  const close = FilePreviewStore(({ close }) => close);
  const download = useDownloadSpaceFile();
  const canDownload = useCanDownloadSpace(file?.space ?? null);
  const preview = useSpaceFilePreview(file);

  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [page, setPage] = useState<{ current: number; total: number } | null>(null);
  /**
   * A file that arrived whole and could not be drawn — a document the renderer choked on.
   *
   * Kept apart from the request's own state because it is a different failure with the same
   * answer: the bytes are here, nothing is retried by reloading them, and what the reader wants
   * is the file itself.
   */
  const [cannotRender, setCannotRender] = useState(false);

  // A new file opens where every document opens: at its own size, on its first page.
  useEffect(() => {
    setZoom(DEFAULT_ZOOM);
    setPage(null);
    setCannotRender(false);
  }, [file]);

  const onRenderError = useCallback(() => setCannotRender(true), []);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    },
    [close],
  );

  useEffect(() => {
    if (!file) return;

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [file, onKeyDown]);

  if (!file) return null;

  const kind = previewKindOf(preview.contentType);
  const onDownload = () => void download({ fileId: file.fileId, space: file.space });

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-[rgba(8,17,32,0.66)] backdrop-blur-[24px]">
      <FilePreviewHeader
        canDownload={canDownload}
        fileName={file.fileName}
        onClose={close}
        onDownload={onDownload}
        onZoomIn={() => setZoom(zoomIn)}
        onZoomOut={() => setZoom(zoomOut)}
        // Only a PDF is read in pages; everything else is one scroll and has no count to show.
        page={kind === PreviewKind.PDF ? page : null}
        zoom={zoom}
      />

      {/* The scroller centers what fits and keeps a margin around what does not: a centering flex
          container narrower than its content pushes the overflow past its own left edge, where no
          scrollbar reaches it, so the track is `min-w-full w-fit` rather than the scroller itself. */}
      <div className="custom-scrollbar min-h-0 flex-1 overflow-auto">
        <div className="flex min-h-full w-fit min-w-full justify-center p-6">
          {preview.state === FilePreviewState.LOADING && <Loader />}

          {preview.state === FilePreviewState.READY && preview.url && !cannotRender && (
            <>
              {kind === PreviewKind.IMAGE && (
                <img
                  alt={file.fileName}
                  className="h-fit max-w-none bg-white object-contain"
                  src={preview.url}
                  style={{ width: (PAGE_WIDTH_PX * zoom) / 100 }}
                />
              )}
              {kind === PreviewKind.PDF && (
                <FilePreviewDocument onPageChange={setPage} url={preview.url} zoom={zoom} />
              )}
              {kind === PreviewKind.DOCX && preview.blob && (
                <FilePreviewDocx blob={preview.blob} onError={onRenderError} zoom={zoom} />
              )}
              {kind === PreviewKind.SHEET && preview.blob && (
                <FilePreviewSheet
                  blob={preview.blob}
                  contentType={preview.contentType ?? ''}
                  onError={onRenderError}
                  zoom={zoom}
                />
              )}
            </>
          )}

          {(cannotRender ||
            preview.state === FilePreviewState.FAILED ||
            preview.state === FilePreviewState.UNSUPPORTED) && (
            <div className="flex flex-col items-center justify-center gap-4 text-sm font-[450] text-white">
              <AppFormattedMessage
                id={
                  preview.state === FilePreviewState.UNSUPPORTED
                    ? 'FILE_CANNOT_BE_PREVIEWED'
                    : 'FILE_PREVIEW_FAILED'
                }
              />
              {/* The one thing still on offer when the file cannot be shown — if they may take it. */}
              {canDownload && (
                <Button
                  className="w-fit px-6 py-2"
                  onClick={onDownload}
                  styleType="BLUE_ROUNDED"
                  type="button"
                >
                  <AppFormattedMessage id="DOWNLOAD" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
